/** @format */

import { useFeatureFlagVariantKey } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useCTAEligibility } from "@/hooks/useCTAEligibility";
import { type CTAId, useCTAStore } from "@/stores/ctaStore";
import { trackEvent } from "@/utils/analytics";

const DEFAULT_PREFIX: Record<CTAId, string> = {
  "sign-up": "signup",
  "pwa-install": "pwa_install",
  notifications: "notification",
  "favorite-drivers": "favorite_drivers",
};

interface CTAControlOptions {
  experimentKey: string;
  isEligible: boolean;
  /** Override the event name prefix (defaults to a per-id mapping). */
  eventPrefix?: string;
}

interface CTAControlResult {
  isActive: boolean;
  variant: string | null;
  dismiss: () => void;
  trackClick: (extra?: Record<string, unknown>) => void;
}

/**
 * Combines CTA eligibility, PostHog experiment variant, and tracking. Each
 * banner becomes a thin wrapper around this hook.
 *
 * Variant `"disabled"` forces ineligibility so an experiment can hide a CTA
 * without code changes. Every event carries the resolved variant.
 */
export function useCTAControl(
  id: CTAId,
  { experimentKey, isEligible, eventPrefix }: CTAControlOptions
): CTAControlResult {
  const rawVariant = useFeatureFlagVariantKey(experimentKey);
  const variant = typeof rawVariant === "string" ? rawVariant : null;

  const eligibility = isEligible && variant !== "disabled";
  const isActive = useCTAEligibility(id, eligibility);

  const prefix = eventPrefix ?? DEFAULT_PREFIX[id];
  const shownFiredRef = useRef(false);

  useEffect(() => {
    if (isActive && !shownFiredRef.current) {
      shownFiredRef.current = true;
      trackEvent(`${prefix}_banner_shown`, { variant });
    }
  }, [isActive, prefix, variant]);

  const dismiss = () => {
    trackEvent(`${prefix}_banner_dismissed`, { variant });
    useCTAStore.getState().dismiss(id);
  };

  const trackClick = (extra?: Record<string, unknown>) => {
    trackEvent(`${prefix}_banner_cta_clicked`, { variant, ...extra });
  };

  return { isActive, variant, dismiss, trackClick };
}
